import {
  approvedScenarioForRole,
  approvedScopesForAction,
} from '../permission-center-ui/DisposablePermissionPreview.fixture';
import {
  buildPermissionBusinessFieldRows,
  type PermissionBusinessFieldRow,
  permissionTargetForBusinessField,
} from './businessFieldDirectory';
import { disposableDepartmentId } from './departmentCompanyMapping';

export { disposableDepartmentId } from './departmentCompanyMapping';

import type {
  CpqState,
  DisposableFieldGrant,
  DisposablePermissionActionContext,
  DisposablePermissionScope,
} from './types';

const readDependencies: Record<string, string> = {
  'customer.manage': 'customer.read',
  'catalog.manage': 'catalog.read',
  'solution.manage': 'solution.read',
  'quote.edit': 'quote.read',
  'quote.finalize': 'quote.read',
  'quote.share.internal': 'quote.read',
  'quote.share.external': 'quote.read',
  'quote.submit': 'quote.read',
  'quote.export': 'quote.read',
  'inventory.adjust': 'inventory.read',
};

function resolveActorContext(
  state: Pick<CpqState, 'catalog'> &
    Partial<Pick<CpqState, 'currentActorUserId'>>,
  roleId: string,
  supplied?: DisposablePermissionActionContext,
): DisposablePermissionActionContext | undefined {
  const users = state.catalog.users.filter(
    (user) => user.status === 'active' && user.roleId === roleId,
  );
  const actorId = supplied?.userId ?? state.currentActorUserId;
  if (!actorId || (supplied && actorId !== state.currentActorUserId))
    return undefined;
  const actor = users.find((user) => user.id === actorId);
  if (!actor) return undefined;
  const departmentId = disposableDepartmentId(actor.department);
  if (supplied && supplied.actorDepartmentId !== departmentId) return undefined;
  return supplied ?? { userId: actor.id, actorDepartmentId: departmentId };
}

export function quotePermissionContext(
  state: Pick<CpqState, 'catalog' | 'currentActorUserId'>,
  quote: CpqState['quotes'][string],
): DisposablePermissionActionContext | undefined {
  const actor = state.catalog.users.find(
    (user) => user.id === state.currentActorUserId && user.status === 'active',
  );
  if (!actor) return undefined;
  const assigned = quote.customer.managerUserId
    ? state.catalog.users.find(
        (user) => user.id === quote.customer.managerUserId,
      )
    : undefined;
  return {
    userId: actor.id,
    actorDepartmentId: disposableDepartmentId(actor.department),
    resourceDepartmentId: assigned
      ? disposableDepartmentId(assigned.department)
      : undefined,
    ownerUserId: quote.customer.managerUserId,
    assignedUserId: quote.customer.managerUserId,
  };
}

function scopeAllows(
  scope: DisposablePermissionScope,
  specifiedDepartmentIds: string[],
  context: DisposablePermissionActionContext,
): boolean {
  if (scope === 'all') return true;
  if (scope === 'none') return false;
  if (scope === 'department' || scope === 'department_and_descendants') {
    // The Disposable catalogue has no hierarchy. Equality is the only
    // descendant relation that can be proven without inventing one.
    return Boolean(
      context.resourceDepartmentId &&
        context.resourceDepartmentId === context.actorDepartmentId,
    );
  }
  if (scope === 'specified_departments') {
    return Boolean(
      context.resourceDepartmentId &&
        specifiedDepartmentIds.includes(context.resourceDepartmentId),
    );
  }
  if (scope === 'self') return context.ownerUserId === context.userId;
  return context.assignedUserId === context.userId;
}

function resolveActionWithoutDependency(
  state: Pick<CpqState, 'catalog'> &
    Partial<Pick<CpqState, 'currentActorUserId'>>,
  roleId: string,
  featureId: string,
  actionId: string,
  quoteState: 'draft' | 'submitted',
  context?: DisposablePermissionActionContext,
): {
  allowed: boolean;
  permission?: string;
  context?: DisposablePermissionActionContext;
} {
  const role = state.catalog.roles.find((item) => item.id === roleId);
  const policy = state.catalog.permissionGovernance.roles[roleId];
  if (role?.status !== 'active' || !policy?.featureEnabled[featureId]) {
    return { allowed: false };
  }
  const approved = approvedScenarioForRole(role.key)
    .features.find((feature) => feature.id === featureId)
    ?.actions.find((action) => action.id === actionId);
  const current = policy.actions[actionId];
  const actorContext = resolveActorContext(state, roleId, context);
  const knownDepartments = new Set(
    state.catalog.users.map((user) => disposableDepartmentId(user.department)),
  );
  if (
    !approved ||
    !current?.enabled ||
    !approved.baseAllowed ||
    approved.explicitDeny ||
    !approvedScopesForAction(approved.scope).includes(current.scope) ||
    !Array.isArray(current.specifiedDepartmentIds) ||
    new Set(current.specifiedDepartmentIds).size !==
      current.specifiedDepartmentIds.length ||
    (current.scope === 'specified_departments' &&
      current.specifiedDepartmentIds.length === 0) ||
    (current.scope !== 'specified_departments' &&
      current.specifiedDepartmentIds.length > 0) ||
    current.specifiedDepartmentIds.some((id) => !knownDepartments.has(id)) ||
    !actorContext ||
    !scopeAllows(current.scope, current.specifiedDepartmentIds, actorContext) ||
    policy.memberAdjustments[actorContext.userId]?.deniedPermissionIds.includes(
      approved.permission,
    )
  )
    return { allowed: false };
  if (quoteState === 'submitted' && approved.permission === 'quote.edit') {
    return { allowed: false };
  }
  return {
    allowed: true,
    permission: approved.permission,
    context: actorContext,
  };
}

export function resolveDisposablePermissionAction(
  state: Pick<CpqState, 'catalog'> &
    Partial<Pick<CpqState, 'currentActorUserId'>>,
  roleId: string,
  featureId: string,
  actionId: string,
  quoteState: 'draft' | 'submitted' = 'draft',
  context?: DisposablePermissionActionContext,
): boolean {
  const resolved = resolveActionWithoutDependency(
    state,
    roleId,
    featureId,
    actionId,
    quoteState,
    context,
  );
  if (!resolved.allowed || !resolved.permission || !resolved.context)
    return false;
  const dependency = readDependencies[resolved.permission];
  if (!dependency) return true;
  const role = state.catalog.roles.find((item) => item.id === roleId);
  const dependencyAction = role
    ? approvedScenarioForRole(role.key)
        .features.find((feature) => feature.id === featureId)
        ?.actions.find((action) => action.permission === dependency)
    : undefined;
  return Boolean(
    dependencyAction &&
      resolveActionWithoutDependency(
        state,
        roleId,
        featureId,
        dependencyAction.id,
        quoteState,
        resolved.context,
      ).allowed,
  );
}

const channelAction: Record<
  string,
  { featureId: string; actionId: string; grant: keyof DisposableFieldGrant }
> = {
  'channel:internal-read': {
    featureId: 'quotes',
    actionId: 'quotes:quote.read',
    grant: 'view',
  },
  'channel:internal-edit': {
    featureId: 'quotes',
    actionId: 'quotes:quote.edit',
    grant: 'edit',
  },
  'channel:internal-share': {
    featureId: 'quotes',
    actionId: 'quotes:quote.share.internal',
    grant: 'view',
  },
  'channel:public-link': {
    featureId: 'quotes',
    actionId: 'quotes:quote.share.external',
    grant: 'view',
  },
  'channel:pdf': {
    featureId: 'quotes',
    actionId: 'quotes:quote.export',
    grant: 'export',
  },
  'channel:excel': {
    featureId: 'quotes',
    actionId: 'quotes:quote.export',
    grant: 'export',
  },
  'channel:document': {
    featureId: 'quotes',
    actionId: 'quotes:quote.export',
    grant: 'export',
  },
};

export interface ChannelPermissionRequirement {
  featureId: string;
  actionId: string;
  grant: keyof DisposableFieldGrant;
}

/** Stable action/grant mapping consumed by strict output-combination checks. */
export function channelPermissionRequirement(
  channelId: string,
): ChannelPermissionRequirement | undefined {
  return channelAction[channelId];
}

/** Channel availability is independent from the field grants it later gates. */
export function disposableChannelEnabled(
  state: Pick<CpqState, 'catalog'>,
  roleId: string,
  channelId: string,
): boolean {
  const role = state.catalog.roles.find((item) => item.id === roleId);
  const policy = state.catalog.permissionGovernance.roles[roleId];
  const approved = role
    ? approvedScenarioForRole(role.key).channels.find(
        (item) => item.id === channelId,
      )
    : undefined;
  return Boolean(
    role?.status === 'active' &&
      approved?.decision === 'allow' &&
      policy?.channels[channelId]?.enabled,
  );
}

export function channelRequiredFieldGrant(
  channelId: string,
): keyof DisposableFieldGrant | undefined {
  return channelAction[channelId]?.grant;
}

/** Channel policy only narrows an independently approved action and field grant. */
export function resolveDisposableChannelFieldIds(
  state: Pick<CpqState, 'catalog'> &
    Partial<Pick<CpqState, 'currentActorUserId'>>,
  roleId: string,
  channelId: string,
  context?: DisposablePermissionActionContext,
): string[] {
  const role = state.catalog.roles.find((item) => item.id === roleId);
  const policy = state.catalog.permissionGovernance.roles[roleId];
  const channel = role
    ? approvedScenarioForRole(role.key).channels.find(
        (item) => item.id === channelId,
      )
    : undefined;
  const published = policy?.channels[channelId];
  const required = channelAction[channelId];
  if (
    role?.status !== 'active' ||
    channel?.decision !== 'allow' ||
    !published?.enabled ||
    !required ||
    !resolveDisposablePermissionAction(
      state,
      roleId,
      required.featureId,
      required.actionId,
      'draft',
      context,
    )
  )
    return [];
  const rows = new Map(
    buildPermissionBusinessFieldRows(state.catalog.quoteFieldDefinitions).map(
      (row) => [row.id, row],
    ),
  );
  return published.fieldIds.filter((fieldId) => {
    const row = rows.get(fieldId);
    return Boolean(
      row?.available &&
        resolveFieldGrantForRow(
          state,
          roleId,
          fieldId,
          required.grant,
          'draft',
          context,
          row,
        ),
    );
  });
}

export function resolveDisposableFieldGrant(
  state: Pick<CpqState, 'catalog'> &
    Partial<Pick<CpqState, 'currentActorUserId'>>,
  roleId: string,
  fieldId: string,
  grantName: keyof DisposableFieldGrant,
  quoteState: 'draft' | 'submitted',
  context?: DisposablePermissionActionContext,
): boolean {
  const row = buildPermissionBusinessFieldRows(
    state.catalog.quoteFieldDefinitions,
  ).find((item) => item.id === fieldId);
  return resolveFieldGrantForRow(
    state,
    roleId,
    fieldId,
    grantName,
    quoteState,
    context,
    row,
  );
}

/** Reuse only field metadata within one synchronous projection, never grants. */
export function createDisposableFieldGrantResolver(
  state: Pick<CpqState, 'catalog'> &
    Partial<Pick<CpqState, 'currentActorUserId'>>,
) {
  let rows: Map<string, PermissionBusinessFieldRow> | undefined;
  return (
    roleId: string,
    fieldId: string,
    grantName: keyof DisposableFieldGrant,
    quoteState: 'draft' | 'submitted',
    context?: DisposablePermissionActionContext,
  ) => {
    rows ??= new Map(
      buildPermissionBusinessFieldRows(state.catalog.quoteFieldDefinitions).map(
        (row) => [row.id, row],
      ),
    );
    return resolveFieldGrantForRow(
      state,
      roleId,
      fieldId,
      grantName,
      quoteState,
      context,
      rows.get(fieldId),
    );
  };
}

function resolveFieldGrantForRow(
  state: Pick<CpqState, 'catalog'> &
    Partial<Pick<CpqState, 'currentActorUserId'>>,
  roleId: string,
  fieldId: string,
  grantName: keyof DisposableFieldGrant,
  quoteState: 'draft' | 'submitted',
  context: DisposablePermissionActionContext | undefined,
  row: PermissionBusinessFieldRow | undefined,
): boolean {
  const grant =
    state.catalog.permissionGovernance.roles[roleId]?.fieldGrants[fieldId];
  const target = row ? permissionTargetForBusinessField(row) : undefined;
  const actionId =
    grantName === 'edit'
      ? target?.editActionId
      : grantName === 'export'
        ? target?.exportActionId
        : target?.readActionId;
  const actionFeatureId =
    grantName === 'export'
      ? (target?.exportFeatureId ?? target?.featureId)
      : target?.featureId;
  const ownerReadAllowed = Boolean(
    target &&
      resolveDisposablePermissionAction(
        state,
        roleId,
        target.featureId,
        target.readActionId,
        quoteState,
        context,
      ),
  );
  return Boolean(
    row?.available &&
      grant?.view &&
      grant[grantName] &&
      (grantName !== 'edit' || row.editable) &&
      target &&
      actionId &&
      actionFeatureId &&
      ownerReadAllowed &&
      resolveDisposablePermissionAction(
        state,
        roleId,
        actionFeatureId,
        actionId,
        quoteState,
        context,
      ),
  );
}

/**
 * Enterprise calculated fields inherit the strictest grant of every direct
 * and transitive field operand. System calculations intentionally stop at
 * their own approved field policy: their typed resolver is governed as one
 * system field and must not be reinterpreted as an enterprise formula.
 *
 * When `allowedFieldIds` is supplied (an output channel), every factor must
 * also be explicitly present in that channel policy even though it does not
 * have to be emitted as an output column.
 */
export function resolveRecursiveEnterpriseFieldGrant(
  state: Pick<CpqState, 'catalog'> &
    Partial<Pick<CpqState, 'currentActorUserId'>>,
  roleId: string,
  fieldId: string,
  grantName: keyof DisposableFieldGrant,
  quoteState: 'draft' | 'submitted',
  context?: DisposablePermissionActionContext,
  allowedFieldIds?: ReadonlySet<string>,
  visiting = new Set<string>(),
): boolean {
  if (
    (allowedFieldIds && !allowedFieldIds.has(fieldId)) ||
    !resolveDisposableFieldGrant(
      state,
      roleId,
      fieldId,
      grantName,
      quoteState,
      context,
    )
  )
    return false;
  if (!fieldId.startsWith('definition:')) return true;
  const key = fieldId.slice('definition:'.length);
  const definition = state.catalog.quoteFieldDefinitions.find(
    (candidate) => candidate.key === key,
  );
  if (!definition) return false;
  if (
    definition.governance.kind !== 'custom' ||
    definition.calculation === undefined
  )
    return true;
  if (!definition.enabled || visiting.has(key)) return false;
  const nextVisiting = new Set(visiting).add(key);
  return definition.calculation.operands.every((operand) =>
    operand.kind === 'constant'
      ? true
      : resolveRecursiveEnterpriseFieldGrant(
          state,
          roleId,
          `definition:${operand.fieldKey}`,
          grantName,
          quoteState,
          context,
          allowedFieldIds,
          nextVisiting,
        ),
  );
}

export function activeRoleIdForState(
  state: Pick<
    CpqState,
    'catalog' | 'role' | 'currentRoleId' | 'currentActorUserId'
  >,
): string | undefined {
  const role = state.catalog.roles.find(
    (candidate) =>
      candidate.id === state.currentRoleId && candidate.status === 'active',
  );
  const actor = state.catalog.users.find(
    (candidate) =>
      candidate.id === state.currentActorUserId &&
      candidate.status === 'active',
  );
  return role && actor && role.key === state.role && actor.roleId === role.id
    ? role.id
    : undefined;
}

/**
 * A non-resource presentation check still requires a persisted member.  It is
 * intentionally only suitable for navigation and configuration entry points;
 * reads and writes of a quote must pass quotePermissionContext instead.
 */
export function activeActorPermissionContext(
  state: Pick<CpqState, 'catalog' | 'currentActorUserId'>,
): DisposablePermissionActionContext | undefined {
  const actor = state.catalog.users.find(
    (user) => user.id === state.currentActorUserId && user.status === 'active',
  );
  return actor
    ? {
        userId: actor.id,
        actorDepartmentId: disposableDepartmentId(actor.department),
      }
    : undefined;
}

export function canUseActivePermissionAction(
  state: Pick<
    CpqState,
    'catalog' | 'role' | 'currentRoleId' | 'currentActorUserId'
  >,
  featureId: string,
  actionId: string,
): boolean {
  const roleId = activeRoleIdForState(state);
  return Boolean(
    roleId &&
      resolveDisposablePermissionAction(
        state,
        roleId,
        featureId,
        actionId,
        'draft',
        // This is only an entry capability: use the selected member's own
        // department/assignment to prove that a scoped action has a possible
        // resource. Actual business reads/writes pass their real resource context.
        (() => {
          const actor = activeActorPermissionContext(state);
          return actor
            ? {
                ...actor,
                resourceDepartmentId: actor.actorDepartmentId,
                ownerUserId: actor.userId,
                assignedUserId: actor.userId,
              }
            : undefined;
        })(),
      ),
  );
}

/** Presentation/command guard for a catalog field under the explicitly
 * selected member. Resource-bearing quote fields must use the quote helpers. */
export function canUseActiveFieldGrant(
  state: Pick<
    CpqState,
    'catalog' | 'role' | 'currentRoleId' | 'currentActorUserId'
  >,
  fieldId: string,
  grant: keyof DisposableFieldGrant,
): boolean {
  const roleId = activeRoleIdForState(state);
  return Boolean(
    roleId &&
      resolveDisposableFieldGrant(
        state,
        roleId,
        fieldId,
        grant,
        'draft',
        activeActorPermissionContext(state),
      ),
  );
}

export function canUseActiveRecursiveEnterpriseFieldGrant(
  state: Pick<
    CpqState,
    'catalog' | 'role' | 'currentRoleId' | 'currentActorUserId'
  >,
  fieldId: string,
  grant: keyof DisposableFieldGrant,
): boolean {
  const roleId = activeRoleIdForState(state);
  return Boolean(
    roleId &&
      resolveRecursiveEnterpriseFieldGrant(
        state,
        roleId,
        fieldId,
        grant,
        'draft',
        activeActorPermissionContext(state),
      ),
  );
}

export function canUseQuotePermissionAction(
  state: Pick<
    CpqState,
    'catalog' | 'role' | 'currentRoleId' | 'currentActorUserId'
  >,
  quote: CpqState['quotes'][string],
  actionId: string,
): boolean {
  const roleId = activeRoleIdForState(state);
  return Boolean(
    roleId &&
      resolveDisposablePermissionAction(
        state,
        roleId,
        'quotes',
        actionId,
        actionId === 'quotes:quote.edit'
          ? 'draft'
          : quote.status === 'draft'
            ? 'draft'
            : 'submitted',
        quotePermissionContext(state, quote),
      ),
  );
}

export function canViewQuoteSourceField(
  state: Pick<
    CpqState,
    'catalog' | 'role' | 'currentRoleId' | 'currentActorUserId'
  >,
  quote: CpqState['quotes'][string],
  fieldId: string,
): boolean {
  const roleId = activeRoleIdForState(state);
  return Boolean(
    roleId &&
      resolveDisposableFieldGrant(
        state,
        roleId,
        fieldId,
        'view',
        quote.status === 'draft' ? 'draft' : 'submitted',
        quotePermissionContext(state, quote),
      ),
  );
}

export function canEditQuoteSourceField(
  state: Pick<
    CpqState,
    'catalog' | 'role' | 'currentRoleId' | 'currentActorUserId'
  >,
  quote: CpqState['quotes'][string],
  fieldId: string,
): boolean {
  const roleId = activeRoleIdForState(state);
  return (
    quote.status === 'draft' &&
    Boolean(
      roleId &&
        resolveDisposableFieldGrant(
          state,
          roleId,
          fieldId,
          'edit',
          'draft',
          quotePermissionContext(state, quote),
        ),
    )
  );
}
